import React, { useState } from 'react';
import {
  Box,
  Typography,
  Container,
  IconButton,
  InputAdornment,
  TextField,
  alpha,
  useTheme,
  Chip,
  Paper,
  Tooltip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

// Icons
import SendIcon from '@mui/icons-material/Send';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import ChatIcon from '@mui/icons-material/Chat';
import DescriptionIcon from '@mui/icons-material/Description';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import HistoryIcon from '@mui/icons-material/History';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import KeyboardVoiceIcon from '@mui/icons-material/KeyboardVoice';
import AttachFileIcon from '@mui/icons-material/AttachFile';

import { useAuth } from '../../contexts/AuthContext';
import AudioRecorder from '../../components/AudioRecorder';
import OnboardingTour from '../../components/OnboardingTour';
import DashboardSkeleton from '../../components/skeletons/DashboardSkeleton';
import { useChatSessions } from '../../hooks/useChat';
import { useDocuments } from '../../hooks/useDocuments';

const getGreeting = (): string => {
  const h = new Date().getHours();
  if (h < 12) return 'Bom dia';
  if (h < 18) return 'Boa tarde';
  return 'Boa noite';
};

interface FeatureChip {
  id: string;
  label: string;
  icon: React.ReactElement;
  route: string;
  color: string;
}

const FEATURES: FeatureChip[] = [
  {
    id: 'generate',
    label: 'Gerar documento',
    icon: <NoteAddIcon sx={{ fontSize: 18 }} />,
    route: '/documents/flows',
    color: '#10b981',
  },
  {
    id: 'chat',
    label: 'Tirar dúvida jurídica',
    icon: <ChatIcon sx={{ fontSize: 18 }} />,
    route: '/chat',
    color: '#3b82f6',
  },
  {
    id: 'documents',
    label: 'Meus documentos',
    icon: <DescriptionIcon sx={{ fontSize: 18 }} />,
    route: '/documents',
    color: '#8b5cf6',
  },
  {
    id: 'alerts',
    label: 'Alertas de processos',
    icon: <NotificationsActiveIcon sx={{ fontSize: 18 }} />,
    route: '/alertas',
    color: '#f59e0b',
  },
];

const DashboardPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [quickChatInput, setQuickChatInput] = useState('');

  const { isLoading: sessionsLoading } = useChatSessions(0, 1);
  const { isLoading: docsLoading } = useDocuments();

  const loading = sessionsLoading || docsLoading;

  const handleQuickChatSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!quickChatInput.trim()) return;
    navigate('/chat', { state: { initialMessage: quickChatInput.trim() } });
  };

  const handleQuickAudioRecording = (audioFile: File) => {
    navigate('/chat', { state: { initialAudioFile: audioFile } });
  };

  const handleFeatureClick = (route: string) => {
    navigate(route);
  };

  if (loading) return <DashboardSkeleton />;

  const firstName = user?.firstName || user?.nome_completo?.split(' ')[0] || 'Advogado';

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 64px)',
        background: `linear-gradient(180deg, 
          ${alpha(theme.palette.background.default, 1)} 0%, 
          ${alpha(theme.palette.primary.main, 0.02)} 50%,
          ${alpha(theme.palette.background.default, 1)} 100%)`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 4,
      }}
    >
      <Container maxWidth="md" sx={{ textAlign: 'center' }}>
        {/* Greeting */}
        <Typography
          variant="h6"
          sx={{
            color: 'text.secondary',
            fontWeight: 400,
            mb: 1,
          }}
        >
          {getGreeting()}, {firstName}
        </Typography>

        {/* Main Question */}
        <Typography
          variant="h3"
          sx={{
            fontWeight: 600,
            mb: 5,
            background: `linear-gradient(135deg, ${theme.palette.text.primary} 0%, ${theme.palette.primary.main} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
          }}
        >
          Como posso ajudar?
        </Typography>

        {/* Chat Input */}
        <Paper
          component="form"
          onSubmit={handleQuickChatSubmit}
          elevation={0}
          data-tour="chat-input"
          sx={{
            p: 0.5,
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
            bgcolor: alpha(theme.palette.background.paper, 0.8),
            backdropFilter: 'blur(10px)',
            transition: 'all 0.3s ease',
            '&:hover': {
              borderColor: alpha(theme.palette.primary.main, 0.3),
              boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
            },
            '&:focus-within': {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
            },
          }}
        >
          <TextField
            fullWidth
            multiline
            minRows={1}
            maxRows={4}
            value={quickChatInput}
            onChange={(e) => setQuickChatInput(e.target.value)}
            placeholder="Pergunte qualquer coisa sobre direito..."
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleQuickChatSubmit();
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3,
                fontSize: '1.1rem',
                '& fieldset': { border: 'none' },
                '&:hover fieldset': { border: 'none' },
                '&.Mui-focused fieldset': { border: 'none' },
              },
              '& .MuiInputBase-input': {
                py: 2,
                px: 2.5,
              },
              '& .MuiInputBase-inputMultiline': {
                lineHeight: 1.5,
              },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ alignSelf: 'flex-end', pb: 1, pr: 1 }}>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <AudioRecorder
                      onRecordingComplete={handleQuickAudioRecording}
                      disabled={false}
                    />
                    <IconButton
                      type="submit"
                      disabled={!quickChatInput.trim()}
                      sx={{
                        bgcolor: quickChatInput.trim()
                          ? theme.palette.primary.main
                          : alpha(theme.palette.action.disabled, 0.1),
                        color: quickChatInput.trim() ? 'white' : 'text.disabled',
                        width: 44,
                        height: 44,
                        '&:hover': {
                          bgcolor: quickChatInput.trim()
                            ? theme.palette.primary.dark
                            : alpha(theme.palette.action.disabled, 0.15),
                        },
                        '&.Mui-disabled': {
                          bgcolor: alpha(theme.palette.action.disabled, 0.1),
                          color: 'text.disabled',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <SendIcon sx={{ fontSize: 20 }} />
                    </IconButton>
                  </Box>
                </InputAdornment>
              ),
            }}
          />
        </Paper>

        {/* Feature Chips */}
        <Box
          sx={{
            mt: 4,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 1.5,
          }}
          data-tour="shortcuts"
        >
          {FEATURES.map((feature) => (
            <Tooltip key={feature.id} title={feature.label} arrow>
              <Chip
                icon={feature.icon}
                label={feature.label}
                onClick={() => handleFeatureClick(feature.route)}
                sx={{
                  py: 2.5,
                  px: 1,
                  fontSize: '0.9rem',
                  fontWeight: 500,
                  borderRadius: 3,
                  bgcolor: alpha(feature.color, 0.08),
                  color: feature.color,
                  border: `1px solid ${alpha(feature.color, 0.2)}`,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '& .MuiChip-icon': {
                    color: feature.color,
                  },
                  '&:hover': {
                    bgcolor: alpha(feature.color, 0.15),
                    borderColor: alpha(feature.color, 0.4),
                    transform: 'translateY(-2px)',
                    boxShadow: `0 4px 12px ${alpha(feature.color, 0.2)}`,
                  },
                }}
              />
            </Tooltip>
          ))}
        </Box>

        {/* Secondary actions row */}
        <Box
          sx={{
            mt: 3,
            display: 'flex',
            justifyContent: 'center',
            gap: 1.5,
          }}
        >
          <Chip
            icon={<HistoryIcon sx={{ fontSize: 16 }} />}
            label="Conversas recentes"
            variant="outlined"
            onClick={() => navigate('/chat')}
            sx={{
              borderColor: alpha(theme.palette.text.secondary, 0.2),
              color: 'text.secondary',
              fontSize: '0.8rem',
              '&:hover': {
                bgcolor: alpha(theme.palette.text.secondary, 0.05),
                borderColor: alpha(theme.palette.text.secondary, 0.3),
              },
            }}
          />
          <Chip
            icon={<AutoAwesomeIcon sx={{ fontSize: 16 }} />}
            label="Documentos recentes"
            variant="outlined"
            onClick={() => navigate('/documents')}
            sx={{
              borderColor: alpha(theme.palette.text.secondary, 0.2),
              color: 'text.secondary',
              fontSize: '0.8rem',
              '&:hover': {
                bgcolor: alpha(theme.palette.text.secondary, 0.05),
                borderColor: alpha(theme.palette.text.secondary, 0.3),
              },
            }}
          />
        </Box>

        {/* Keyboard hint */}
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 4,
            color: alpha(theme.palette.text.secondary, 0.5),
          }}
        >
          Pressione <kbd style={{ 
            padding: '2px 6px', 
            borderRadius: 4, 
            backgroundColor: alpha(theme.palette.divider, 0.3),
            fontSize: '0.75rem',
          }}>Enter</kbd> para enviar
        </Typography>
      </Container>

      <OnboardingTour />
    </Box>
  );
};

export default DashboardPage;
